import { retryCancelablePromise } from '../core/utils/retryCancelablePromise';
export class ScoutService {
    constructor(httpRequest) {
        Object.defineProperty(this, "httpRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: httpRequest
        });
    }
    /**
     * Create a job todo
     * Git provider webhook triggers Scout to create a job todo.
     * Similarly, a lint job started or completed triggers jobs todo.
     * Since Scout is stateless and distributed, this is the API for storing things to do.
     * It's also a way to communicate with Scout by making jobs for it to do too (such as communicating status back to Git).
     * Finally, it can be used to trigger Scout to update again in the case of a GitHub webhook outage.
     * @returns Job Created.
     * @throws ApiError
     */
    createTodo({ orgId, projectId, xRedoclyScoutVersion, requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/scout/todos', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * List all todos
     * @returns JobList OK.
     * @throws ApiError
     */
    getAllTodos({ orgId, projectId, xRedoclyScoutVersion, after, before, sort = '-id', filter, limit = 10, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/scout/todos', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, query: {
                'after': after,
                'before': before,
                'sort': sort,
                'filter': filter,
                'limit': limit,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Process next job
     * Retrieve and start the next available job.
     * @returns Job OK.
     * @throws ApiError
     */
    startNextJob({ orgId, projectId, xRedoclyScoutVersion, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/scout/tasks', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * List all tasks
     * @returns JobList OK.
     * @throws ApiError
     */
    getAllTasks({ orgId, projectId, xRedoclyScoutVersion, after, before, sort = '-id', filter, limit = 10, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/scout/tasks', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, query: {
                'after': after,
                'before': before,
                'sort': sort,
                'filter': filter,
                'limit': limit,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Get task
     * Get task.
     * @returns Job OK.
     * @throws ApiError
     */
    getJobById({ orgId, projectId, jobId, xRedoclyScoutVersion, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'GET', url: '/orgs/{orgId}/projects/{projectId}/scout/tasks/{jobId}', path: {
                'orgId': orgId,
                'projectId': projectId,
                'jobId': jobId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                404: `Resource not found.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Update job
     * Scout sends this API request after completing a job.
     * Supports only `PROCESSING` -> `COMPLETED` and `PROCESSING` -> `FAILED` transitions.
     * @returns Job OK.
     * @throws ApiError
     */
    updateJob({ orgId, projectId, jobId, xRedoclyScoutVersion, requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'PATCH', url: '/orgs/{orgId}/projects/{projectId}/scout/tasks/{jobId}', path: {
                'orgId': orgId,
                'projectId': projectId,
                'jobId': jobId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                404: `Resource not found.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Push status
     * Scout uses this to periodically report it's status back to main api.
     * @returns void
     * @throws ApiError
     */
    reportStatus({ orgId, projectId, xRedoclyScoutVersion, requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/scout/status', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
    /**
     * Get metadata validation result
     * Validate api metadata against "metadataSchema" in project's config file.
     * @returns MetadataValidationResult OK.
     * @throws ApiError
     */
    validateMetadata({ orgId, projectId, xRedoclyScoutVersion, requestBody, }, options) {
        return retryCancelablePromise(() => this.httpRequest.request(Object.assign({ method: 'POST', url: '/orgs/{orgId}/projects/{projectId}/scout/metadata/validate', path: {
                'orgId': orgId,
                'projectId': projectId,
            }, headers: {
                'x-redocly-scout-version': xRedoclyScoutVersion,
            }, body: requestBody, mediaType: 'application/json', errors: {
                400: `Bad Request.`,
                401: `User is not authorized to access this resource.`,
                403: `Forbidden.`,
                404: `Resource not found.`,
                500: `Internal Server Error.`,
            } }, options)), options);
    }
}
//# sourceMappingURL=ScoutService.js.map